import { useCallback } from 'react'
import type { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import {
  LinkSelectionOptions,
  useDocumentPickerIfAvailable,
} from './DocumentPickerContext'
import type {
  DocumentNode,
  DocumentPickerSelection,
} from './DocumentPicker.types'

/**
 * Models whose picks are linked straight from the document picker's
 * selection. Google Drive, SharePoint and Confluence still hand their picks
 * to a legacy picker context that the Add Evidence / Add Document forms
 * consume; a model added since the document picker landed has no such
 * context and is consumed here instead.
 */
export const DIRECT_PICKER_MODEL_IDS: readonly string[] = ['trr-notion']

export const usesDirectPickerSelection = (modelId: string): boolean =>
  DIRECT_PICKER_MODEL_IDS.includes(modelId)

export interface DirectPickerSelection {
  /** The connection the documents were picked from. */
  receptor?: ReceptorRecord
  /** The picked documents, empty when nothing is waiting to be linked. */
  nodes: DocumentNode[]
  clear: () => void
  /** Links the picked documents through Linker.SetLink. */
  link: (options?: LinkSelectionOptions) => Promise<string[]>
}

const noNodes: DocumentNode[] = []

/**
 * The documents picked for linking from a connection that has no legacy
 * picker context, for the form that opened the picker: a pick made for a
 * policy (source 'Policy') is never offered to an evidence form, and the
 * other way round. Safe to call outside the DocumentPickerProvider: it then
 * reports nothing picked.
 */
export const useDirectPickerSelection = (
  source: Exclude<DocumentPickerSelection['source'], 'FolderScan'>,
): DirectPickerSelection => {
  const picker = useDocumentPickerIfAvailable()
  const selection = picker?.selection
  const applies =
    !!selection &&
    selection.source === source &&
    usesDirectPickerSelection(selection.receptor.getModelid())
  const clear = useCallback(() => picker?.clearSelection(), [picker])
  const link = useCallback(
    (options?: LinkSelectionOptions) =>
      picker ? picker.linkSelection(options) : Promise.resolve([]),
    [picker],
  )
  return {
    receptor: applies ? selection.receptor : undefined,
    nodes: applies ? selection.nodes : noNodes,
    clear,
    link,
  }
}
